// ImagePickerDialog — ISP-9999 (gordon-0913-gallery-theme-wallpaper-pattern-
// image-parity). Gordon's UX note (2026-09-13 10:00 PT): keep the Gallery Theme
// panel compact — the Image tab's sources (Upload from device, Media Library
// picker, Recent media, Embed URL — the same set the Edit-Wheel builder's
// Image tab offers, see galleryTheme.js's inventory note) live behind a
// "Choose image" popup instead of expanding the panel column inline.
//
// Every source below is IMPORTED, not reimplemented (everything-is-a-shared-
// component): UploadMedia / RecentMedia / EmbedURL / MediaBrowser are the
// exact components the builder's own Image tab (Wallpaper/children/Image/
// index.jsx) and the canonical Segment Fill picker (SegmentBuilder/Shared/
// MediaSelection) mount, and MediaLibraryReuse is the app-wide "Reuse from
// your Media Library" affordance (its own button + dialog) every
// media-attaching surface adopts — nesting its dialog inside this one is the
// pattern that component's own header comment documents (DL-048: "EVERY
// media-attaching surface... mounts the exact same reuse experience through a
// single component").
//
// gordon-0923-P2-gallery-theme-image-picker-missing-browse-and-all-caps-tabs:
// this dialog had drifted from every other picker in the app in two ways —
// (1) it was MISSING "Browse" (the stock-image-search source MediaSelection's
// default `categories` and the Wallpaper Image tab both offer) and (2) it
// rendered the tab strip with raw MUI <Tabs>/<Tab>, which defaults
// textTransform to uppercase and doesn't share ANY of the shared `Tabs`
// component's sizing/spacing/hover/focus-ring styling every other picker's
// tab strip gets for free. Fixed by mounting the same shared `Tabs` component
// (../Tabs) MediaSelection and Wallpaper/children/Image both use, and adding
// Browse via the same `MediaBrowser` component MediaSelection mounts for its
// own "Browse" category.
//
// `pathType="profile"` (added to UploadMedia/EmbedURL by this unit) routes the
// upload through the user's own profile-asset namespace instead of a
// wheel-scoped one, since the Gallery Theme wallpaper has no wheel/segment
// context — see UploadMedia's own doc comment.
import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import Tabs from "../Tabs";
import UploadMedia from "../SegmentBuilder/Shared/UploadMedia";
import MediaLibraryReuse from "../MediaLibrary/MediaLibraryReuse";
import RecentMedia from "../SegmentBuilder/Shared/RecentMedia";
import EmbedURL from "../SegmentBuilder/Shared/EmbedURL";
import MediaBrowser from "../SegmentBuilder/Shared/MediaBrowser";
import { T } from "./constants";

// Labels double as the shared `Tabs` component's category values (its own
// convention — see Tabs/index.jsx). Order matches the canonical MediaSelection
// picker's own convention (its own header comment: Preset ordered before
// "Recent", "Recent" always last) — Upload, Embed URL, Browse, then the
// pick-an-existing-image sources grouped together with Recent staying last.
const SOURCES = ["Upload", "Embed URL", "Browse", "Media Library", "Recent"];

export default function ImagePickerDialog({ open, onClose, onSelect }) {
  const muiTheme = useTheme();
  const fullScreen = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const [source, setSource] = useState("Upload");

  const commit = (url, sourceLabel) => {
    if (!url) return;
    onSelect({ url, source: sourceLabel });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      aria-labelledby="image-picker-title"
    >
      <DialogTitle
        id="image-picker-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: T.fontBody,
        }}
      >
        Choose image
        <IconButton aria-label="Close image picker" onClick={onClose} size="small">
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <Tabs
        categories={SOURCES}
        selectedCategory={source}
        onCategoryChange={(_, value) => setSource(value)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        ariaLabel="Image source"
        sx={{ borderBottom: `1px solid ${T.line}`, px: "16px" }}
      />
      <DialogContent>
        {source === "Upload" && (
          <UploadMedia
            type="image"
            pathType="profile"
            allowGif={false}
            handleMedia={(m) => {
              if (!m.loading && m.src) commit(m.src, "upload");
            }}
          />
        )}
        {source === "Media Library" && (
          <Box sx={{ py: "8px" }}>
            <MediaLibraryReuse
              acceptedTypes={["image"]}
              contextLabel="Gallery Theme wallpaper"
              label="Browse Media Library"
              onInsert={(value) => commit(value?.src || value?.url, "library")}
            />
          </Box>
        )}
        {source === "Recent" && (
          <RecentMedia
            type="image"
            showDropdown={false}
            onClickMedia={(item) => commit(item?.src || item?.url, "recent")}
          />
        )}
        {source === "Embed URL" && (
          <EmbedURL
            type="image"
            pathType="profile"
            onConfirm={(value) => {
              if (value && !value.loading) commit(value.src, "url");
            }}
          />
        )}
        {source === "Browse" && (
          <MediaBrowser
            mediaType="image"
            isGifAllowed={false}
            onConfirm={(image) => commit(image?.src || image, "browse")}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
